import moment from "moment";
import { convertFromUTC, convertToUTC } from "utils/datetime";

export const HOURLY_RESIZE_SNAP_MINUTES = 15;
export const HOURLY_RESIZE_MIN_DURATION_MINUTES = 15;

/** Arrastre del bloque completo (todos los periodos) por el eje horario. */
export const HOURLY_MOVE_EDGE = "move";

/**
 * Mismo criterio que `_overlapInterval` en Plan.js: un intervalo que no suma
 * horas de trabajo pinta como disponibilidad (`isAvailability` de la celda).
 */
export const isAvailabilityTimeInterval = (interval) =>
	!interval?.time_type?.counts_as_total_hours;

export const timeToMinutes = (hhmm) => {
	if (!hhmm || typeof hhmm !== "string") return 0;
	const [hours, minutes] = hhmm.split(":").map(Number);
	return hours * 60 + minutes;
};

export const minutesToTime = (totalMinutes) => {
	const dayMinutes = 24 * 60;
	const normalized = ((totalMinutes % dayMinutes) + dayMinutes) % dayMinutes;
	const hours = Math.floor(normalized / 60);
	const minutes = normalized % 60;
	return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const DAY_MINUTES = 24 * 60;

/**
 * 24h degenerado: la API guarda start===end (p. ej. 22:00Z-22:00Z → 00:00-00:00 local).
 * En el reloj del día eso es [start, start+24h), no un intervalo de duración 0.
 */
export const isLocal24hDegenerate = (startHHmm, endHHmm) =>
	!!startHHmm && startHHmm === endHHmm;

/**
 * Día completo no laboral: start===end, o el 24h que fixStartEndIntervals
 * reescribe como start → start-1min (p. ej. 00:00-23:59).
 */
export const isFullDayLocalInterval = (startHHmm, endHHmm) => {
	if (!startHHmm || !endHHmm) return false;
	if (isLocal24hDegenerate(startHHmm, endHHmm)) return true;
	return getLocalIntervalDurationMinutes(startHHmm, endHHmm) >= DAY_MINUTES - 1;
};

export const isFullDayTimeInterval = (interval) => {
	if (!interval) return false;
	const start = convertFromUTC(interval.start);
	const end = convertFromUTC(interval.render_end || interval.end);
	return isFullDayLocalInterval(start, end);
};

/** True si hay al menos un tramo que no cubre el día entero (vacación/ausencia por hora). */
export const hasPartialDayIntervals = (time_intervals) => {
	if (!time_intervals?.length) return false;
	return time_intervals.some((interval) => !isFullDayTimeInterval(interval));
};

export const resolveLocalDayViewEnd = (startHHmm, endHHmm) => {
	if (!startHHmm || !endHHmm) return endHHmm;
	if (startHHmm === endHHmm || endHHmm === "24:00") return "24:00";
	return endHHmm;
};

export const localIntervalEndMinutes = (startHHmm, endHHmm) => {
	if (endHHmm === "24:00") return DAY_MINUTES;
	const startMin = timeToMinutes(startHHmm);
	const endMin = timeToMinutes(endHHmm);
	if (endMin === startMin) return startMin + DAY_MINUTES;
	if (endMin === 0 && startMin !== 0) return DAY_MINUTES;
	return endMin;
};

export const snapMinutes = (
	minutes,
	step = HOURLY_RESIZE_SNAP_MINUTES
) => Math.round(minutes / step) * step;

/**
 * Duración en minutos entre start/end locales (HH:mm).
 * Si end <= start se interpreta como cruce de medianoche.
 */
export const getLocalIntervalDurationMinutes = (startHHmm, endHHmm) => {
	const start = timeToMinutes(startHHmm);
	let end = timeToMinutes(endHHmm);
	if (end <= start) {
		end += 24 * 60;
	}
	return end - start;
};

/**
 * Un intervalo same-day no puede "fliparse" a overnight al arrastrar un extremo
 * sobre el otro: getLocalIntervalDurationMinutes lo interpretaría como ~24h y
 * pasaría el mínimo. Overnight real (origen end <= start) sigue usando duración wrap.
 */
export const isValidResizedLocalInterval = (
	originalStartHHmm,
	originalEndHHmm,
	nextStartHHmm,
	nextEndHHmm
) => {
	const originalStart = timeToMinutes(originalStartHHmm);
	const nextStart = timeToMinutes(nextStartHHmm);
	const nextEnd = localIntervalEndMinutes(nextStartHHmm, nextEndHHmm);
	const originalIsOvernight =
		!isLocal24hDegenerate(originalStartHHmm, originalEndHHmm) &&
		timeToMinutes(originalEndHHmm) < originalStart;

	if (!originalIsOvernight) {
		if (nextStart < 0 || nextEnd > DAY_MINUTES) return false;
		return nextEnd - nextStart >= HOURLY_RESIZE_MIN_DURATION_MINUTES;
	}

	return (
		getLocalIntervalDurationMinutes(nextStartHHmm, nextEndHHmm) >=
		HOURLY_RESIZE_MIN_DURATION_MINUTES
	);
};

export const addDaysYmd = (day, days) =>
	moment(day, "YYYY-MM-DD").add(days, "days").format("YYYY-MM-DD");

export const daysBetweenYmd = (fromDay, toDay) =>
	moment(toDay, "YYYY-MM-DD").diff(moment(fromDay, "YYYY-MM-DD"), "days");

const clockDurationMinutes = (localStart, localEnd) => {
	if (!localStart || !localEnd) return 0;
	if (isLocal24hDegenerate(localStart, localEnd)) return DAY_MINUTES;
	if (localEnd === "24:00") {
		return DAY_MINUTES - timeToMinutes(localStart);
	}
	const startMin = timeToMinutes(localStart);
	const endMin = timeToMinutes(localEnd);
	if (endMin === 0 && startMin !== 0) {
		return DAY_MINUTES - startMin;
	}
	if (endMin <= startMin) {
		return endMin + DAY_MINUTES - startMin;
	}
	return endMin - startMin;
};

const localClockFromAbs = (absMin) => {
	const clock = ((absMin % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
	return minutesToTime(clock);
};

const localEndFromAbs = (endAbs) => {
	if (endAbs % DAY_MINUTES === 0) return "24:00";
	return localClockFromAbs(endAbs);
};

/**
 * Intervalos API → segmentos en minutos absolutos desde el día 0 (día del assignment).
 * Encadena tramos 00:00 que siguen a un fin a medianoche (turno largo >24h).
 */
export const intervalsToAbsSegments = (intervals) => {
	if (!intervals?.length) return null;

	const segments = [];
	let chainEndAbs = null;

	for (const interval of intervals) {
		const localStart = convertFromUTC(interval.start);
		const localEnd = convertFromUTC(interval.end);
		if (!localStart || !localEnd) return null;

		const startClock = timeToMinutes(localStart);
		const duration = clockDurationMinutes(localStart, localEnd);
		if (duration < HOURLY_RESIZE_MIN_DURATION_MINUTES) return null;

		const prevEndedMidnight =
			chainEndAbs != null && chainEndAbs > 0 && chainEndAbs % DAY_MINUTES === 0;
		const isContinuation = prevEndedMidnight && startClock === 0;

		let startAbs;
		if (isContinuation) {
			startAbs = chainEndAbs;
		} else if (chainEndAbs == null) {
			startAbs = startClock;
		} else {
			const prevStartAbs = segments[segments.length - 1].startAbs;
			const prevDayOffset = Math.floor(prevStartAbs / DAY_MINUTES);
			startAbs = prevDayOffset * DAY_MINUTES + startClock;
			if (startAbs < prevStartAbs) {
				startAbs += DAY_MINUTES;
			}
		}

		const endAbs = startAbs + duration;
		segments.push({
			startAbs,
			endAbs,
			isAvailability: isAvailabilityTimeInterval(interval),
			interval,
		});
		chainEndAbs = endAbs;
	}

	return mergeTouchingAbsSegments(segments);
};

const mergeTouchingAbsSegments = (segments) => {
	if (!segments?.length) return segments;
	const merged = [{ ...segments[0] }];
	for (let i = 1; i < segments.length; i++) {
		const prev = merged[merged.length - 1];
		const cur = segments[i];
		if (
			prev.endAbs === cur.startAbs &&
			!!prev.isAvailability === !!cur.isAvailability
		) {
			prev.endAbs = cur.endAbs;
		} else {
			merged.push({ ...cur });
		}
	}
	return merged;
};

const sliceAbsRangeToDayClocks = (startAbs, endAbs) => {
	const slices = [];
	let cursor = startAbs;
	while (cursor < endAbs) {
		const dayOffset = Math.floor(cursor / DAY_MINUTES);
		const nextMidnight = (dayOffset + 1) * DAY_MINUTES;
		const sliceEnd = Math.min(endAbs, nextMidnight);
		slices.push({
			dayOffset,
			start: localClockFromAbs(cursor),
			end: localEndFromAbs(sliceEnd),
		});
		cursor = sliceEnd;
	}
	return slices;
};

const utcEndFromLocal = (localEnd) =>
	convertToUTC(localEnd === "24:00" ? "00:00" : localEnd);

const cloneIntervalAtClock = (interval, localStart, localEnd, keepId) => {
	const next = {
		...interval,
		start: convertToUTC(localStart),
		end: utcEndFromLocal(localEnd),
	};
	if (!keepId) {
		delete next.id;
	}
	return next;
};

/**
 * Rebaja segmentos al día 0 del nuevo assignment y serializa a time_intervals API.
 * Un tramo ≤24h que cruza medianoche se guarda como overnight (end < start).
 * Un tramo >24h se parte en rebanadas por día calendario (turno largo continuo).
 */
export const absSegmentsToIntervalResult = (segments) => {
	if (!segments?.length) return null;

	const minStart = Math.min(...segments.map((item) => item.startAbs));
	const dayShift = Math.floor(minStart / DAY_MINUTES);
	const rebased = segments.map((item) => ({
		...item,
		startAbs: item.startAbs - dayShift * DAY_MINUTES,
		endAbs: item.endAbs - dayShift * DAY_MINUTES,
	}));

	const intervals = [];
	rebased.forEach((seg) => {
		const duration = seg.endAbs - seg.startAbs;
		if (duration < HOURLY_RESIZE_MIN_DURATION_MINUTES) return;

		if (duration <= DAY_MINUTES) {
			const startDay = Math.floor(seg.startAbs / DAY_MINUTES);
			const endDay = Math.floor((seg.endAbs - 1) / DAY_MINUTES);
			const startClock = localClockFromAbs(seg.startAbs);
			if (startDay === endDay) {
				intervals.push(
					cloneIntervalAtClock(
						seg.interval,
						startClock,
						localEndFromAbs(seg.endAbs),
						true
					)
				);
				return;
			}
			intervals.push(
				cloneIntervalAtClock(
					seg.interval,
					startClock,
					localClockFromAbs(seg.endAbs),
					true
				)
			);
			return;
		}

		sliceAbsRangeToDayClocks(seg.startAbs, seg.endAbs).forEach((slice, index) => {
			intervals.push(
				cloneIntervalAtClock(
					seg.interval,
					slice.start,
					slice.end,
					index === 0
				)
			);
		});
	});

	if (!intervals.length) return null;
	return { intervals, dayShift };
};

export const groupAbsSegmentsByDayOffset = (segments) => {
	const byDay = {};
	(segments || []).forEach((seg) => {
		sliceAbsRangeToDayClocks(seg.startAbs, seg.endAbs).forEach((slice) => {
			if (!byDay[slice.dayOffset]) byDay[slice.dayOffset] = [];
			byDay[slice.dayOffset].push({
				start: slice.start,
				end: slice.end,
				isAvailability: !!seg.isAvailability,
			});
		});
	});
	return byDay;
};

/** Rebanadas por día calendario para pintar / copiar overnight+largo. */
export const splitAssignmentIntervalsByDay = (intervals) => {
	const segments = intervalsToAbsSegments(intervals);
	if (!segments?.length) {
		return [{ dayOffset: 0, intervals: intervals || [] }];
	}

	const byDay = {};
	segments.forEach((seg) => {
		sliceAbsRangeToDayClocks(seg.startAbs, seg.endAbs).forEach((slice, index) => {
			if (!byDay[slice.dayOffset]) byDay[slice.dayOffset] = [];
			byDay[slice.dayOffset].push(
				cloneIntervalAtClock(
					seg.interval,
					slice.start,
					slice.end,
					slice.dayOffset === 0 && index === 0
				)
			);
		});
	});

	return Object.keys(byDay)
		.map(Number)
		.sort((a, b) => a - b)
		.map((dayOffset) => ({
			dayOffset,
			intervals: byDay[dayOffset],
		}));
};

export const getIntervalsAbsDurationMinutes = (intervals) => {
	const segments = intervalsToAbsSegments(intervals);
	if (!segments?.length) return 0;
	return (
		Math.max(...segments.map((item) => item.endAbs)) -
		Math.min(...segments.map((item) => item.startAbs))
	);
};

const rawIntervalsOfAssignment = (assignment) => {
	if (assignment?.time_intervals_span?.length) {
		return assignment.time_intervals_span;
	}
	return cardIntervalsOfAssignment(assignment) || [];
};

/**
 * Periodo que pinta la tarjeta de un día (Funciones, Turnos, Ubicaciones).
 * `time_intervals_span` junta los días encadenados para la vista Horas; en la
 * tarjeta ese bloque repetiría el mismo periodo una vez por día.
 */
export const cardIntervalsOfAssignment = (assignment) => {
	if (!assignment) return null;
	if (assignment.time_intervals?.length) return assignment.time_intervals;
	if (assignment.time_intervals_from_shift) {
		return assignment.shift?.time_intervals ?? null;
	}
	return assignment.shift?.time_intervals ?? null;
};

export const intervalListEndsAtLocalMidnight = (intervals) => {
	const segments = intervalsToAbsSegments(intervals);
	if (!segments?.length) return false;
	const endAbs = Math.max(...segments.map((item) => item.endAbs));
	return endAbs > 0 && endAbs % DAY_MINUTES === 0;
};

export const intervalListStartsAtLocalMidnight = (intervals) => {
	const segments = intervalsToAbsSegments(intervals);
	if (!segments?.length) return false;
	const startAbs = Math.min(...segments.map((item) => item.startAbs));
	return startAbs % DAY_MINUTES === 0;
};

const withoutAssignmentId = (list, assignmentId) =>
	(list || []).filter((item) => item.id !== assignmentId);

/**
 * Encadena assignments consecutivos del mismo turno que se tocan a medianoche.
 * Así un turno >24h persistido día a día se manipula como un bloque continuo
 * en vista Horas. No toca `plan.assignments` (otras vistas siguen viendo un
 * bloque por día).
 */
export const linkTouchingAssignmentsAcrossDays = (plans) => {
	if (!plans?.length) return plans;

	const byDay = new Map(plans.map((plan) => [plan.day, plan]));
	const consumed = new Set();
	const ordered = [...plans].sort((a, b) => a.day.localeCompare(b.day));

	ordered.forEach((plan) => {
		(plan.assignments || []).forEach((assignment) => {
			if (consumed.has(assignment.id)) return;
			const originIntervals = rawIntervalsOfAssignment(assignment);
			if (!intervalListEndsAtLocalMidnight(originIntervals)) return;

			const chain = [{ plan, assignment, intervals: originIntervals }];
			let cursorDay = plan.day;
			let cursorIntervals = originIntervals;

			while (intervalListEndsAtLocalMidnight(cursorIntervals)) {
				const nextDay = addDaysYmd(cursorDay, 1);
				const nextPlan = byDay.get(nextDay);
				if (!nextPlan) break;
				const next = (nextPlan.assignments || []).find(
					(item) =>
						!consumed.has(item.id) &&
						item.id !== assignment.id &&
						item.shift?.id === assignment.shift?.id &&
						intervalListStartsAtLocalMidnight(rawIntervalsOfAssignment(item))
				);
				if (!next) break;
				const nextIntervals = rawIntervalsOfAssignment(next);
				consumed.add(next.id);
				chain.push({ plan: nextPlan, assignment: next, intervals: nextIntervals });
				cursorDay = nextDay;
				cursorIntervals = nextIntervals;
			}

			if (chain.length < 2) return;

			const merged = chain.flatMap((item) => item.intervals);
			assignment.time_intervals_span = merged;
			assignment.span_origin_day = plan.day;
			assignment.span_parts = chain.map((item) => ({
				id: item.assignment.id,
				day: item.plan.day,
			}));
			assignment.is_overnight = true;
			const slices = splitAssignmentIntervalsByDay(merged);
			const day0 = slices.find((slice) => slice.dayOffset === 0);
			if (day0?.intervals?.length) {
				assignment.time_intervals_for_day = day0.intervals;
			}

			chain.slice(1).forEach((item, index) => {
				const slice = slices.find((entry) => entry.dayOffset === index + 1);
				const copy = {
					...assignment,
					span_origin_day: plan.day,
					span_day_offset: index + 1,
					is_overnight: true,
					time_intervals_overnight: slice?.intervals || item.intervals,
					time_intervals_for_day: slice?.intervals || item.intervals,
				};
				item.plan.plan_assignments_with_overnight = withoutAssignmentId(
					item.plan.plan_assignments_with_overnight ||
						item.plan.assignments,
					item.assignment.id
				);
				item.plan.plan_assignments_with_overnight.push(copy);
			});
		});
	});

	return plans;
};

export const collectAssignmentPaintDays = (
	originDay,
	intervals,
	extraDays
) => {
	const days = new Set();
	if (originDay) days.add(originDay);
	(extraDays || []).forEach((day) => {
		if (day) days.add(day);
	});
	splitAssignmentIntervalsByDay(intervals).forEach((slice) => {
		if (originDay) {
			days.add(addDaysYmd(originDay, slice.dayOffset));
		}
	});
	return days;
};

/**
 * En una celda-hora, el Descanso por defecto se pinta en todas las columnas.
 * Si ya hay un turno (overnight, disponibilidad, trabajo), no hay que apilar
 * el bloque gris debajo.
 */
export const dropRestWhenShiftPaintsHour = (
	hourAssignments,
	defaultShiftId
) => {
	const list = hourAssignments || [];
	const isRestHa = (ha) =>
		!!ha?.isRest ||
		(defaultShiftId != null &&
			ha.assignment?.shift?.id === defaultShiftId);
	const hasShiftPaint = list.some((ha) => ha?.pinta && !isRestHa(ha));
	if (!hasShiftPaint) return list;
	return list.filter((ha) => !isRestHa(ha));
};

export const getAssignmentEnvelopeLocal = (intervals) => {
	if (!intervals?.length) return null;

	const first = intervals[0];
	const last = intervals[intervals.length - 1];
	const start = convertFromUTC(first.start);
	const end = convertFromUTC(last.end);

	if (!start || !end) return null;

	return {
		start,
		end: resolveLocalDayViewEnd(start, end),
		firstIndex: 0,
		lastIndex: intervals.length - 1,
	};
};

const resolveDeltaIntervalIndex = (intervals, edge, intervalIndex) => {
	if (edge === HOURLY_MOVE_EDGE) return 0;
	if (
		intervalIndex != null &&
		intervalIndex >= 0 &&
		intervalIndex < intervals.length
	) {
		return intervalIndex;
	}
	return edge === "start" ? 0 : intervals.length - 1;
};

/**
 * Aplica resize/move en minutos absolutos (puede cruzar medianoche y durar >24h).
 * `dayShift` es cuántos días hay que sumar al `day` del assignment.
 */
export const applyHourlyShiftDelta = (
	intervals,
	edge,
	deltaMinutes,
	intervalIndex
) => {
	if (!intervals?.length) return null;
	if (!deltaMinutes) {
		return { intervals, dayShift: 0 };
	}

	const segments = intervalsToAbsSegments(intervals);
	if (!segments?.length) return null;

	if (edge === HOURLY_MOVE_EDGE) {
		const moved = segments.map((seg) => ({
			...seg,
			startAbs: seg.startAbs + deltaMinutes,
			endAbs: seg.endAbs + deltaMinutes,
		}));
		return absSegmentsToIntervalResult(moved);
	}

	if (edge !== "start" && edge !== "end") return null;

	const resolvedIndex = Math.min(
		resolveDeltaIntervalIndex(intervals, edge, intervalIndex),
		segments.length - 1
	);
	const target = segments[resolvedIndex];
	if (!target) return null;

	let nextStartAbs = target.startAbs;
	let nextEndAbs = target.endAbs;
	if (edge === "start") {
		nextStartAbs = target.startAbs + deltaMinutes;
	} else {
		nextEndAbs = target.endAbs + deltaMinutes;
	}

	if (nextEndAbs - nextStartAbs < HOURLY_RESIZE_MIN_DURATION_MINUTES) {
		return null;
	}

	if (edge === "start" && resolvedIndex > 0) {
		if (nextStartAbs < segments[resolvedIndex - 1].endAbs) return null;
	}
	if (edge === "end" && resolvedIndex < segments.length - 1) {
		if (nextEndAbs > segments[resolvedIndex + 1].startAbs) return null;
	}

	const nextSegments = segments.map((seg, index) =>
		index === resolvedIndex
			? { ...seg, startAbs: nextStartAbs, endAbs: nextEndAbs }
			: seg
	);
	return absSegmentsToIntervalResult(nextSegments);
};

/**
 * Desplaza todos los periodos el mismo delta. Puede cambiar de día y
 * convertirse en overnight / turno largo (>24h).
 */
export const applyMoveDeltaToIntervals = (intervals, deltaMinutes) => {
	const result = applyHourlyShiftDelta(
		intervals,
		HOURLY_MOVE_EDGE,
		deltaMinutes,
		0
	);
	return result?.intervals ?? null;
};

/**
 * Aplica un delta en minutos (ya snappeado) al inicio o fin de un intervalo,
 * o al turno entero si `edge === "move"`. Puede cruzar medianoche y durar >24h.
 */
export const applyResizeDeltaToIntervals = (
	intervals,
	edge,
	deltaMinutes,
	intervalIndex
) => {
	const result = applyHourlyShiftDelta(
		intervals,
		edge,
		deltaMinutes,
		intervalIndex
	);
	return result?.intervals ?? null;
};

export const prepareIntervalsForUpdate = (intervals) =>
	(intervals || []).map((time_interval) => {
		const new_interval = {
			start: time_interval.start,
			end: time_interval.end,
			time_type_id:
				time_interval.time_type_id ?? time_interval.time_type?.id,
		};

		if (!isNaN(time_interval.id)) {
			new_interval.id = time_interval.id;
		}

		return new_interval;
	});

/**
 * Calcula el delta snappeado en minutos a partir del movimiento horizontal
 * y el ancho de una columna-hora.
 */
export const pixelsToSnappedMinutes = (deltaPx, hourCellWidthPx) => {
	if (!hourCellWidthPx || hourCellWidthPx <= 0) return 0;
	const rawMinutes = (deltaPx / hourCellWidthPx) * 60;
	return snapMinutes(rawMinutes);
};

export const formatResizePreviewLabel = (startHHmm, endHHmm) => {
	if (!startHHmm || !endHHmm) return "";
	return `${startHHmm} - ${endHHmm}`;
};

export const formatResizePreviewIntervalsLabel = (localIntervals) => {
	if (!localIntervals?.length) return "";
	return localIntervals
		.map((interval) => formatResizePreviewLabel(interval.start, interval.end))
		.join(", ");
};

export const toLocalIntervalList = (intervals) =>
	(intervals || []).map((interval) => {
		const start = convertFromUTC(interval.start);
		const end = convertFromUTC(interval.end);
		return {
			start,
			end: resolveLocalDayViewEnd(start, end),
			isAvailability: isAvailabilityTimeInterval(interval),
		};
	});

/**
 * Un turno mixto (trabajo + disponibilidad) pinta segmentos de ambos tipos.
 * Cada segmento debe previsualizarse contra los periodos de su mismo tipo,
 * si no un bloque de disponibilidad tomaría la geometría del periodo de trabajo.
 */
export const filterPreviewIntervalsByKind = (
	previewIntervals,
	isAvailability
) => {
	if (!previewIntervals?.length || isAvailability == null) {
		return previewIntervals || [];
	}
	const matching = previewIntervals.filter(
		(interval) => !!interval.isAvailability === !!isAvailability
	);
	return matching.length ? matching : previewIntervals;
};

export const getIntervalIndexForResizeHandle = (hourNumber, intervals, edge) => {
	const localIntervals = toLocalIntervalList(intervals);
	if (!localIntervals.length) return -1;

	if (edge === "start") {
		for (let i = 0; i < localIntervals.length; i++) {
			const overlap = getHourBlockOverlapWithLocalInterval(
				hourNumber,
				localIntervals[i].start,
				localIntervals[i].end
			);
			if (overlap?.isFirst) {
				return i;
			}
		}
		return 0;
	}

	if (edge === "end") {
		for (let i = localIntervals.length - 1; i >= 0; i--) {
			const overlap = getHourBlockOverlapWithLocalInterval(
				hourNumber,
				localIntervals[i].start,
				localIntervals[i].end
			);
			if (overlap?.isLast) {
				return i;
			}
		}
		return localIntervals.length - 1;
	}

	return -1;
};

const buildPreviewFromIntervals = (nextIntervals, dayShift, resolvedIndex) => {
	const segments = intervalsToAbsSegments(nextIntervals);
	const byOffset = groupAbsSegmentsByDayOffset(segments || []);
	const day0 = byOffset[0] || [];
	const previewByDayOffset = {};
	Object.keys(byOffset).forEach((key) => {
		const intervalsForDay = byOffset[key];
		previewByDayOffset[key] = {
			intervals: intervalsForDay,
			previewStart: intervalsForDay[0]?.start,
			previewEnd: intervalsForDay[intervalsForDay.length - 1]?.end,
		};
	});

	return {
		previewStart: day0[0]?.start,
		previewEnd: day0[day0.length - 1]?.end,
		previewIntervals: day0.length ? day0 : toLocalIntervalList(nextIntervals),
		previewByDayOffset,
		dayShift: dayShift || 0,
		nextIntervals,
		intervalIndex: resolvedIndex,
		isMultiInterval: nextIntervals.length > 1,
	};
};

export const getResizePreviewState = (
	intervals,
	edge,
	deltaMinutes,
	intervalIndex
) => {
	if (!intervals?.length) return null;

	const resolvedIndex = resolveDeltaIntervalIndex(
		intervals,
		edge,
		intervalIndex
	);

	// delta 0 = volver al origen (cancelar sin PUT).
	if (deltaMinutes === 0) {
		return buildPreviewFromIntervals(intervals, 0, resolvedIndex);
	}

	const applied = applyHourlyShiftDelta(
		intervals,
		edge,
		deltaMinutes,
		intervalIndex
	);
	if (!applied?.intervals) return null;

	return buildPreviewFromIntervals(
		applied.intervals,
		applied.dayShift,
		resolvedIndex
	);
};

export const getPreviewForPlanDay = (resizePreview, planDay) => {
	if (!resizePreview?.previewByDayOffset || !resizePreview.day || !planDay) {
		return null;
	}
	const originDay = addDaysYmd(
		resizePreview.day,
		resizePreview.dayShift || 0
	);
	const offset = daysBetweenYmd(originDay, planDay);
	return resizePreview.previewByDayOffset[offset] || null;
};

export const getTotalLocalIntervalsDurationHours = (localIntervals) => {
	if (!localIntervals?.length) return 0;
	return localIntervals.reduce(
		(total, interval) =>
			total +
			getLocalIntervalDurationMinutes(interval.start, interval.end) / 60,
		0
	);
};

const getHourBlockOverlapForLocalRange = (
	hourNumber,
	rangeStartHHmm,
	rangeEndHHmm
) => {
	const hour = Number(hourNumber);
	if (Number.isNaN(hour)) return null;

	const blockStartMin = hour * 60;
	const blockEndMin = blockStartMin + 60;

	const rangeStartMin = timeToMinutes(rangeStartHHmm);
	let rangeEndMin =
		rangeEndHHmm === "24:00"
			? DAY_MINUTES
			: timeToMinutes(rangeEndHHmm);
	if (rangeEndMin <= rangeStartMin) {
		rangeEndMin += DAY_MINUTES;
	}

	const candidates = [
		{ start: blockStartMin, end: blockEndMin },
		{ start: blockStartMin + 24 * 60, end: blockEndMin + 24 * 60 },
	];

	for (const block of candidates) {
		const overlapStart = Math.max(block.start, rangeStartMin);
		const overlapEnd = Math.min(block.end, rangeEndMin);
		if (overlapStart >= overlapEnd) continue;

		const localBlockStart = block.start % (24 * 60);
		return {
			start_offset: (overlapStart - block.start) / 60,
			count: (overlapEnd - overlapStart) / 60,
			isFirst:
				rangeStartMin >= block.start && rangeStartMin < block.end,
			isLast: rangeEndMin > block.start && rangeEndMin <= block.end,
			localBlockStart,
		};
	}

	return null;
};

/**
 * Solape de un intervalo local [start, end) con la columna-hora.
 */
export const getHourBlockOverlapWithLocalInterval = (
	hourNumber,
	startHHmm,
	endHHmm
) => {
	if (!startHHmm || !endHHmm) return null;
	return getHourBlockOverlapForLocalRange(hourNumber, startHHmm, endHHmm);
};

/**
 * Solape de varios intervalos locales con la columna-hora (turnos partidos).
 * Devuelve el primer segmento que intersecta la hora; no rellena huecos entre intervalos.
 */
export const getHourBlockOverlapWithLocalIntervals = (
	hourNumber,
	localIntervals
) => {
	if (!localIntervals?.length) return null;

	for (const interval of localIntervals) {
		const overlap = getHourBlockOverlapWithLocalInterval(
			hourNumber,
			interval.start,
			interval.end
		);
		if (overlap) {
			return { ...overlap, isAvailability: !!interval.isAvailability };
		}
	}

	return null;
};

/**
 * Solape del envelope previsto [previewStart, previewEnd) con la columna-hora.
 * Devuelve null si no hay que pintar ese bloque durante el resize preview.
 */
export const getHourBlockOverlapWithPreview = (
	hourNumber,
	previewStart,
	previewEnd
) => {
	if (
		hourNumber === undefined ||
		hourNumber === null ||
		!previewStart ||
		!previewEnd
	) {
		return null;
	}

	return getHourBlockOverlapForLocalRange(
		hourNumber,
		previewStart,
		previewEnd
	);
};

/**
 * Solape en preview de resize: usa intervalos discretos si existen (multi-periodo).
 */
export const getHourBlockOverlapForResizePreview = (
	hourNumber,
	resizePreview,
	segmentIsAvailability = null
) => {
	if (!resizePreview) return null;

	if (resizePreview.previewIntervals?.length) {
		return getHourBlockOverlapWithLocalIntervals(
			hourNumber,
			filterPreviewIntervalsByKind(
				resizePreview.previewIntervals,
				segmentIsAvailability
			)
		);
	}

	return getHourBlockOverlapWithPreview(
		hourNumber,
		resizePreview.previewStart,
		resizePreview.previewEnd
	);
};

/**
 * Intervalos efectivos del assignment (manual → shift).
 */
export const getAssignmentIntervalsForResize = (assignment) => {
	if (!assignment) return null;
	if (assignment.time_intervals?.length) {
		return assignment.time_intervals;
	}
	if (assignment.time_intervals_from_shift) {
		return assignment.shift?.time_intervals ?? null;
	}
	return assignment.shift?.time_intervals ?? null;
};

/** Turno partido / multi-intervalo (p. ej. D12h 11:30–15:00 + 16:00–20:30). */
export const isSplitOrMultiIntervalAssignment = (assignment) => {
	const intervals = getAssignmentIntervalsForResize(assignment);
	return (intervals?.length ?? 0) > 1;
};

export const isResizeableHourlyAssignment = (assignment, isWorkLikeFn) => {
	if (!assignment?.shift) return false;
	if (assignment.id === -1) return false;
	if (typeof isWorkLikeFn === "function" && !isWorkLikeFn(assignment.shift.type)) {
		return false;
	}
	const intervals = getAssignmentIntervalsForResize(assignment);
	if (!intervals?.length) {
		return false;
	}
	return true;
};

/**
 * Horas locales (0–23) que intersectan [previewStart, previewEnd) en vista del día.
 */
export const getHourNumbersForLocalEnvelope = (previewStart, previewEnd) => {
	if (!previewStart || !previewEnd) return new Set();

	const startMin = timeToMinutes(previewStart);
	let endMin =
		previewEnd === "24:00" ? DAY_MINUTES : timeToMinutes(previewEnd);
	if (endMin <= startMin) {
		endMin += DAY_MINUTES;
	}

	const hours = new Set();
	const startHour = Math.floor(startMin / 60);
	const endExclusiveHour = Math.ceil(endMin / 60);

	for (let h = startHour; h < endExclusiveHour; h++) {
		hours.add(((h % 24) + 24) % 24);
	}

	return hours;
};

/**
 * Une la lista de columnas-hora con las necesarias para el preview de resize.
 */
export const expandWorkingHoursListForPreview = (
	hoursList,
	previewStart,
	previewEnd
) => {
	if (!hoursList?.length || !previewStart || !previewEnd) {
		return hoursList || [];
	}

	const needed = getHourNumbersForLocalEnvelope(previewStart, previewEnd);
	const set = new Set(hoursList);
	needed.forEach((h) => set.add(h));

	let list = [...set].sort((a, b) => a - b);

	if (
		list.length > 1 &&
		list[0] > 0 &&
		list[list.length - 1] === 0
	) {
		list = list.slice(0, -1);
	}

	return list;
};

const createEmptyHourCell = (hourNum) => ({
	hour: hourNum,
	count: 0,
	hour_assignments: [],
	pinta: false,
	halfHourInfo: [],
});

/**
 * Inserta columnas-hora faltantes en day_plan.hours para alinear celdas durante el preview.
 */
export const mergeDayHoursForResizePreview = (
	dayHours,
	previewStart,
	previewEnd
) => {
	if (!dayHours?.length || !previewStart || !previewEnd) {
		return dayHours || [];
	}

	const currentNums = dayHours.map((h) => Number(h.hour));
	const expandedNums = expandWorkingHoursListForPreview(
		currentNums,
		previewStart,
		previewEnd
	);

	if (expandedNums.length === currentNums.length) {
		return dayHours;
	}

	const byHour = new Map(dayHours.map((h) => [Number(h.hour), h]));

	return expandedNums.map((hourNum) => {
		if (byHour.has(hourNum)) {
			return byHour.get(hourNum);
		}
		return createEmptyHourCell(hourNum);
	});
};

export const findAssignmentInDayPlan = (dayPlan, assignmentId) => {
	if (!dayPlan?.hours || assignmentId == null) return null;

	for (const hour of dayPlan.hours) {
		for (const ha of hour.hour_assignments || []) {
			if (ha.assignment?.id === assignmentId) {
				return ha.assignment;
			}
		}
	}
	return null;
};

/**
 * En celdas nuevas (sin pinta), inyecta el segmento del turno en resize preview.
 */
export const enrichHourForResizePreview = (hour, dayPlan, resizePreview) => {
	const dayPreview = getPreviewForPlanDay(resizePreview, dayPlan?.day);
	const previewForOverlap = dayPreview
		? {
				...resizePreview,
				previewStart: dayPreview.previewStart,
				previewEnd: dayPreview.previewEnd,
				previewIntervals: dayPreview.intervals,
			}
		: resizePreview;

	if (
		!previewForOverlap?.previewStart ||
		!previewForOverlap?.previewEnd ||
		!resizePreview?.day ||
		!dayPreview
	) {
		return hour;
	}

	const overlap = getHourBlockOverlapForResizePreview(
		hour.hour,
		previewForOverlap
	);
	if (!overlap) {
		return hour;
	}

	const alreadyPainted = (hour.hour_assignments || []).some(
		(ha) =>
			ha.pinta && ha.assignment?.id === resizePreview.assignmentId
	);
	if (alreadyPainted) {
		return hour;
	}

	const assignment =
		resizePreview.assignmentSnapshot ||
		findAssignmentInDayPlan(dayPlan, resizePreview.assignmentId);
	if (!assignment) {
		return hour;
	}

	return {
		...hour,
		pinta: true,
		hour_assignments: [
			...(hour.hour_assignments || []),
			{
				assignment,
				pinta: true,
				count: overlap.count,
				work_count: overlap.isAvailability ? 0 : overlap.count,
				start_offset: overlap.start_offset,
				isFirst: overlap.isFirst,
				isLast: overlap.isLast,
				isAvailability: !!overlap.isAvailability,
			},
		],
	};
};

export const resolvePlanHourCellAtPoint = (
	clientX,
	clientY,
	employeeId
) => {
	if (typeof document === "undefined" || clientX == null || clientY == null) {
		return null;
	}
	const el = document.elementFromPoint(clientX, clientY);
	const td = el?.closest?.("td.shift-cell-hours");
	if (!td?.dataset?.planDay || td.dataset.hour == null) return null;
	if (
		employeeId != null &&
		td.dataset.employeeId &&
		String(td.dataset.employeeId) !== String(employeeId)
	) {
		return null;
	}
	const hour = Number(td.dataset.hour);
	if (Number.isNaN(hour)) return null;
	const rect = td.getBoundingClientRect();
	if (!rect.width) return null;
	const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
	const minutesInHour = snapMinutes(frac * 60);
	const minutes = Math.min(hour * 60 + minutesInHour, DAY_MINUTES);
	return { day: td.dataset.planDay, hour, minutes };
};

const hitToAbsMinutes = (hit, originDay) => {
	if (!hit?.day || !originDay) return null;
	return daysBetweenYmd(originDay, hit.day) * DAY_MINUTES + hit.minutes;
};

export const buildResizePointerHandlers = ({
	edge,
	hourCellEl,
	intervals,
	intervalIndex,
	originDay,
	employeeId,
	onPreview,
	onCommit,
	onCancel,
}) => {
	const hourWidth = hourCellEl?.getBoundingClientRect?.().width || 0;
	if (!hourWidth || !intervals?.length) {
		return null;
	}

	const bodyClass =
		edge === HOURLY_MOVE_EDGE
			? "is-hourly-shift-moving"
			: "is-hourly-shift-resizing";

	let startX = 0;
	let downAbs = null;
	let lastDelta = 0;
	let moved = false;

	const resolveSnappedDelta = (event) => {
		const hit = resolvePlanHourCellAtPoint(
			event.clientX,
			event.clientY,
			employeeId
		);
		const nowAbs = hitToAbsMinutes(hit, originDay);
		if (nowAbs != null && downAbs != null) {
			return snapMinutes(nowAbs - downAbs);
		}
		if (downAbs != null) {
			return lastDelta;
		}
		return pixelsToSnappedMinutes(event.clientX - startX, hourWidth);
	};

	const onPointerMove = (event) => {
		const snapped = resolveSnappedDelta(event);
		if (snapped === lastDelta) return;

		const preview = getResizePreviewState(
			intervals,
			edge,
			snapped,
			intervalIndex
		);
		// Invalid (p. ej. flip / solape): mantener último delta válido.
		// snapped === 0 siempre es válido (origen) vía getResizePreviewState.
		if (!preview) return;

		lastDelta = snapped;
		moved = snapped !== 0;
		onPreview?.({
			edge,
			deltaMinutes: snapped,
			...preview,
		});
	};

	const cleanup = () => {
		document.removeEventListener("pointermove", onPointerMove);
		document.removeEventListener("pointerup", onPointerUp);
		document.removeEventListener("pointercancel", onPointerUp);
		document.body.classList.remove(bodyClass);
	};

	const onPointerUp = () => {
		cleanup();
		// Sin cambio neto (nunca movió, o volvió al origen) → cancelar sin PUT.
		if (!moved || lastDelta === 0) {
			onCancel?.();
			return;
		}

		const preview = getResizePreviewState(
			intervals,
			edge,
			lastDelta,
			intervalIndex
		);
		if (!preview) {
			onCancel?.();
			return;
		}

		onCommit?.({
			edge,
			deltaMinutes: lastDelta,
			...preview,
		});
	};

	return {
		start: (event) => {
			// En move el mismo pointerdown puede acabar siendo un click que abre
			// el modal; cancelarlo pondría en riesgo ese click. La selección de
			// texto ya la corta `user-select` de la clase en body.
			if (edge !== HOURLY_MOVE_EDGE) {
				event.preventDefault();
			}
			event.stopPropagation();
			startX = event.clientX;
			lastDelta = 0;
			moved = false;
			const downHit =
				resolvePlanHourCellAtPoint(
					event.clientX,
					event.clientY,
					employeeId
				) ||
				(hourCellEl?.dataset?.planDay
					? {
							day: hourCellEl.dataset.planDay,
							hour: Number(hourCellEl.dataset.hour),
							minutes: Number(hourCellEl.dataset.hour) * 60 +
								snapMinutes(
									Math.max(
										0,
										Math.min(
											1,
											(event.clientX -
												hourCellEl.getBoundingClientRect().left) /
												(hourCellEl.getBoundingClientRect().width || 1)
										)
									) * 60
								),
						}
					: null);
			downAbs = hitToAbsMinutes(downHit, originDay);
			if (event.currentTarget?.setPointerCapture) {
				try {
					event.currentTarget.setPointerCapture(event.pointerId);
				} catch {
					// noop: algunos targets no admiten capture
				}
			}
			document.body.classList.add(bodyClass);
			document.addEventListener("pointermove", onPointerMove);
			document.addEventListener("pointerup", onPointerUp);
			document.addEventListener("pointercancel", onPointerUp);
		},
	};
};

/** Utilidad de debug / tests: formatea momento local HH:mm */
export const ensureHHmm = (value) => {
	const m = moment(value, ["HH:mm", "HH:mm:ss", "HH:mmZ", "HH:mm:ssZ"], true);
	return m.isValid() ? m.format("HH:mm") : "";
};

/**
 * ¿El envelope del turno exige columnas-hora fuera de la lista actual?
 */
export const resizeNeedsColumnExpand = (
	hoursList,
	nextIntervalsUtc
) => {
	const envelope = getAssignmentEnvelopeLocal(nextIntervalsUtc);
	if (!envelope || !hoursList?.length) return false;
	const expanded = expandWorkingHoursListForPreview(
		hoursList,
		envelope.start,
		envelope.end
	);
	return expanded.length > hoursList.length;
};

/**
 * Reconstruye dayPlan.hours para un assignment tras resize, sin moment() masivo.
 * Usa los mismos solapes que el preview en vivo.
 * `employee` se adjunta al assignment para que la tabla de necesidades
 * (buildQuarterHourCoverageMap) pueda contar cobertura al instante.
 */
const stripAssignmentFromDayPlan = (dayPlan, assignmentId) => {
	if (!dayPlan || assignmentId == null) return dayPlan;

	const hours = (dayPlan.hours || []).map((hour) => {
		const hour_assignments = (hour.hour_assignments || []).filter(
			(ha) => ha.assignment?.id !== assignmentId
		);
		return {
			...hour,
			hour_assignments,
			pinta: hour_assignments.some((ha) => ha.pinta),
		};
	});

	return {
		...dayPlan,
		hours,
		assignments: (dayPlan.assignments || []).filter(
			(item) => item.id !== assignmentId
		),
		plan_assignments_with_overnight: (
			dayPlan.plan_assignments_with_overnight ||
			dayPlan.assignments ||
			[]
		).filter((item) => item.id !== assignmentId),
	};
};

const findAssignmentInEmployeeRow = (row, assignmentId) => {
	if (!row || assignmentId == null) return null;
	for (const dayPlan of row.plans || []) {
		const painted = findAssignmentInDayPlan(dayPlan, assignmentId);
		if (painted) return painted;
		const stored = (dayPlan.assignments || []).find(
			(item) => item.id === assignmentId
		);
		if (stored) return stored;
	}
	return null;
};

/**
 * Reconstruye dayPlan.hours para un assignment tras resize, sin moment() masivo.
 * `paintIntervalsUtc` pinta este día (rebanada); `nextIntervalsUtc` es el payload.
 */
export const applyOptimisticHourlyResizeToDayPlan = (
	dayPlan,
	assignmentId,
	nextIntervalsUtc,
	employee,
	options = {}
) => {
	if (!dayPlan || assignmentId == null || !nextIntervalsUtc?.length) {
		return dayPlan;
	}

	const paintIntervalsUtc = options.paintIntervalsUtc || nextIntervalsUtc;
	const localIntervals = toLocalIntervalList(paintIntervalsUtc);
	if (!localIntervals.length) {
		return dayPlan;
	}

	const envelopeStart = localIntervals[0].start;
	const envelopeEnd = localIntervals[localIntervals.length - 1].end;

	const paintedAssignment = findAssignmentInDayPlan(dayPlan, assignmentId);
	const baseAssignment =
		options.baseAssignment ||
		paintedAssignment ||
		(dayPlan.assignments || []).find((item) => item.id === assignmentId);
	if (!baseAssignment) {
		return dayPlan;
	}

	const isOvernightCopy = !!options.isOvernightCopy;
	const originDay = options.originDay || dayPlan.day;

	const updatedAssignment = {
		...baseAssignment,
		employee: baseAssignment.employee || employee,
		time_intervals: nextIntervalsUtc,
		time_intervals_from_manual: true,
		time_intervals_from_shift: false,
		time_intervals_for_day: paintIntervalsUtc,
		span_origin_day: originDay,
		span_day_offset: options.dayOffset || 0,
		is_overnight: isOvernightCopy || (options.dayOffset || 0) > 0,
		time_intervals_overnight: isOvernightCopy ? paintIntervalsUtc : undefined,
	};

	const assignments = isOvernightCopy
		? (dayPlan.assignments || []).filter((item) => item.id !== assignmentId)
		: (dayPlan.assignments || []).some((item) => item.id === assignmentId)
			? (dayPlan.assignments || []).map((item) =>
					item.id === assignmentId ? updatedAssignment : item
				)
			: [...(dayPlan.assignments || []), updatedAssignment];

	const mergedHours = mergeDayHoursForResizePreview(
		dayPlan.hours || [],
		envelopeStart,
		envelopeEnd
	);

	const hours = mergedHours.map((hour) => {
		const others = (hour.hour_assignments || []).filter(
			(ha) => ha.assignment?.id !== assignmentId
		);

		const nextHourAssignments = [...others];
		for (const interval of localIntervals) {
			const overlap = getHourBlockOverlapWithLocalInterval(
				hour.hour,
				interval.start,
				interval.end
			);
			if (!overlap) continue;
			nextHourAssignments.push({
				assignment: updatedAssignment,
				pinta: true,
				count: overlap.count,
				work_count: interval.isAvailability ? 0 : overlap.count,
				start_offset: overlap.start_offset,
				isFirst: overlap.isFirst,
				isLast: overlap.isLast,
				isAvailability: !!interval.isAvailability,
			});
		}

		const hourAssignments = dropRestWhenShiftPaintsHour(
			nextHourAssignments,
			null
		);

		return {
			...hour,
			pinta: hourAssignments.some((ha) => ha.pinta),
			hour_assignments: hourAssignments,
		};
	});

	const working_hours_list = hours.map((h) => Number(h.hour));

	const overnightList = isOvernightCopy
		? [
				...(dayPlan.plan_assignments_with_overnight || []).filter(
					(item) => item.id !== assignmentId
				),
				updatedAssignment,
			]
		: (dayPlan.plan_assignments_with_overnight || dayPlan.assignments || []).map(
				(item) => (item.id === assignmentId ? updatedAssignment : item)
			);

	const hasInOvernight = overnightList.some((item) => item.id === assignmentId);

	return {
		...dayPlan,
		assignments,
		plan_assignments_with_overnight: hasInOvernight
			? overnightList
			: [...overnightList, updatedAssignment],
		working_hours_list,
		hours,
	};
};

/**
 * Sincroniza plan_assignments_hourly_totals del día con dayPlan.hours.
 * La tabla de necesidades lee los totals, no plans[].hours.
 */
export const syncHourlyTotalsForDay = (hourlyTotals, day, hours) => {
	const list = Array.isArray(hourlyTotals) ? hourlyTotals : [];
	let found = false;
	const next = list.map((dayTotal) => {
		if (dayTotal.day !== day) return dayTotal;
		found = true;
		return { ...dayTotal, hours };
	});
	if (!found && day && hours) {
		next.push({ day, hours });
	}
	return next;
};

export const resizeNeedsColumnExpandOnDays = (
	row,
	nextIntervals,
	nextDay
) => {
	if (!row?.plans || !nextIntervals?.length || !nextDay) return false;
	const slices = splitAssignmentIntervalsByDay(nextIntervals);
	return slices.some((slice) => {
		const day = addDaysYmd(nextDay, slice.dayOffset);
		const dayPlan = row.plans.find((plan) => plan.day === day);
		const hoursList = dayPlan?.working_hours_list;
		if (!hoursList?.length) return true;
		return resizeNeedsColumnExpand(hoursList, slice.intervals);
	});
};

/**
 * Patch optimista de resize/move (puede tocar varios días).
 * También actualiza plan_assignments_hourly_totals para refrescar la tabla
 * de necesidades en el mismo setState (sin esperar getPlanAssignments).
 */
export const applyOptimisticHourlyResizeToPlanAssignments = (
	planAssignments,
	{
		employeeId,
		day,
		assignmentId,
		nextIntervals,
		nextDay,
		originDay,
		assignmentSnapshot,
		prevIntervals,
		spanParts,
		extraStripDays,
	}
) => {
	if (!planAssignments?.length || !employeeId || !day || assignmentId == null) {
		return planAssignments;
	}

	const fromDay = originDay || day;
	const toDay = nextDay || day;

	return planAssignments.map((row) => {
		if (row.employee?.id !== employeeId) {
			return row;
		}

		const baseAssignment =
			assignmentSnapshot ||
			findAssignmentInEmployeeRow(row, assignmentId);
		if (!baseAssignment || !nextIntervals?.length) {
			return row;
		}

		const slices = splitAssignmentIntervalsByDay(nextIntervals);
		const sliceDays = collectAssignmentPaintDays(toDay, nextIntervals, [
			fromDay,
			toDay,
			...((spanParts || []).map((part) => part?.day)),
			...((extraStripDays || [])),
		]);
		collectAssignmentPaintDays(fromDay, prevIntervals).forEach((paintDay) => {
			sliceDays.add(paintDay);
		});

		let nextTotals = row.plan_assignments_hourly_totals;
		const nextPlans = (row.plans || []).map((dayPlan) => {
			if (!sliceDays.has(dayPlan.day)) {
				return dayPlan;
			}

			const stripped = stripAssignmentFromDayPlan(dayPlan, assignmentId);
			const slice = slices.find(
				(item) => addDaysYmd(toDay, item.dayOffset) === dayPlan.day
			);
			if (!slice) {
				nextTotals = syncHourlyTotalsForDay(
					nextTotals,
					dayPlan.day,
					stripped.hours
				);
				return stripped;
			}

			const painted = applyOptimisticHourlyResizeToDayPlan(
				stripped,
				assignmentId,
				nextIntervals,
				row.employee,
				{
					baseAssignment,
					paintIntervalsUtc: slice.intervals,
					isOvernightCopy: slice.dayOffset > 0,
					originDay: toDay,
					dayOffset: slice.dayOffset,
				}
			);
			nextTotals = syncHourlyTotalsForDay(
				nextTotals,
				dayPlan.day,
				painted.hours
			);
			return painted;
		});

		return {
			...row,
			plans: nextPlans,
			plan_assignments_hourly_totals: nextTotals,
		};
	});
};
